import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { cacheReferenceData, getCachedReferenceData } from '@/lib/offline-store';
import { useOffline } from '@/contexts/OfflineContext';

const CACHE_MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24 hours

/**
 * Caches reference data (articles, clients) to IndexedDB when online,
 * and hydrates React Query cache from IndexedDB when offline.
 *
 * Snapshots are addressed by KIND ('articles', 'clients'); the tenant is
 * folded into the storage key by `@/lib/offline-store`, which derives it
 * from the access token. This hook therefore cannot name — and cannot
 * accidentally hydrate — another tenant's snapshot: it has no key to
 * build one from.
 */
export function useOfflineCache() {
  const queryClient = useQueryClient();
  const { isOnline, isOffline } = useOffline();

  // When online: snapshot successful query data to IndexedDB
  useEffect(() => {
    if (!isOnline) return;

    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      if (event.type !== 'updated' || event.action.type !== 'success') return;

      const queryKey = event.query.queryKey;
      const keyStr = JSON.stringify(queryKey);

      // Cache article list responses
      if (keyStr.includes('"articles"') && keyStr.includes('"list"')) {
        cacheReferenceData('articles', event.query.state.data);
      }
      // Cache client list responses
      if (keyStr.includes('"clients"') && keyStr.includes('"list"')) {
        cacheReferenceData('clients', event.query.state.data);
      }
    });

    return unsubscribe;
  }, [isOnline, queryClient]);

  // When going offline: hydrate React Query from IndexedDB
  useEffect(() => {
    if (!isOffline) return;

    async function hydrate() {
      const articles = await getCachedReferenceData('articles');
      if (articles && Date.now() - articles.cachedAt < CACHE_MAX_AGE_MS) {
        queryClient.setQueryData(['articles', 'list', {}], articles.data);
      }

      const clients = await getCachedReferenceData('clients');
      if (clients && Date.now() - clients.cachedAt < CACHE_MAX_AGE_MS) {
        queryClient.setQueryData(['clients', 'list', {}], clients.data);
      }
    }

    hydrate();
  }, [isOffline, queryClient]);
}
